/**
 * LabMode — Phase 1's deliverable: every species generated live, side by side, swimming,
 * with the triangle budget on screen. Change the seed and the whole gallery rebuilds.
 */
import * as THREE from 'three';
import type { Mode, ModeContext } from './Mode.ts';
import type { SharkMesh } from '../voxel/MeshService.ts';
import { Seafloor } from '../render/Seafloor.ts';
import { SharkMaterial, swimParamsForSpeed } from '../render/SharkMaterial.ts';
import { LabPanel, type LabCardState } from '../ui/LabPanel.ts';
import { SPECIES } from '../data/species.ts';
import { LAB, MOVEMENT, PERF, WATER } from '../data/tuning.ts';

interface Slot {
  readonly pivot: THREE.Group;
  readonly material: SharkMaterial;
  mesh: THREE.Mesh | null;
  built: SharkMesh | null;
  card: LabCardState;
}

/** the gallery swims at a steady cruise so the deform reads without any input */
const GALLERY_SPEED_FRACTION = 0.78;

export class LabMode implements Mode {
  readonly name = 'lab' as const;

  /** assigned by main.ts — the lab owns the seed field, main owns the seed */
  onSeedRequest: ((seed: number) => void) | null = null;

  private readonly ctx: ModeContext;
  private readonly group = new THREE.Group();
  private readonly seafloor = new Seafloor();
  private readonly slots: Slot[] = [];
  private panel: LabPanel | null = null;

  private seed = 1;
  private spin = true;
  private buildToken = 0;
  private laidOut = false;

  constructor(ctx: ModeContext) {
    this.ctx = ctx;
    this.group.add(this.seafloor.mesh);

    for (let i = 0; i < SPECIES.length; i++) {
      const pivot = new THREE.Group();
      // broadside to the camera: a gallery shark read from directly astern is a smudge
      pivot.rotation.y = Math.PI * 0.5;
      const material = new SharkMaterial();
      material.applyWaterConfig(WATER);
      // decorrelate the undulation so ten sharks don't beat in lockstep (SPEC §4.3)
      material.setPhase((i * 2.399963) % (Math.PI * 2));
      this.group.add(pivot);
      this.slots.push({
        pivot,
        material,
        mesh: null,
        built: null,
        card: { id: SPECIES[i].id, name: SPECIES[i].name, triangles: 0, voxels: 0, ms: 0, over: false },
      });
    }
  }

  enter(seed: number): void {
    this.seed = seed;
    this.ctx.scene.add(this.group);

    const panel = new LabPanel(this.ctx.overlay);
    panel.setSeed(seed);
    panel.onSeedChange = (s) => this.onSeedRequest?.(s);
    panel.onRandomize = () => this.onSeedRequest?.((Math.floor(performance.now() * 1000) ^ 0x9e3779b9) >>> 0);
    panel.onToggleSpin = (on) => {
      this.spin = on;
    };
    this.panel = panel;

    void this.rebuild();
  }

  exit(): void {
    this.ctx.scene.remove(this.group);
    this.panel?.dispose();
    this.panel = null;
  }

  setSeed(seed: number): void {
    if (seed === this.seed) return;
    this.seed = seed;
    this.panel?.setSeed(seed);
    void this.rebuild();
  }

  private async rebuild(): Promise<void> {
    const token = ++this.buildToken;
    this.laidOut = false;
    this.panel?.setStatus(`GENERATING ${SPECIES.length} GENOMES · SEED ${this.seed}`);

    const t0 = performance.now();
    const results = await Promise.allSettled(
      SPECIES.map((def) => this.ctx.mesh.build(def.genome(this.seed))),
    );
    if (token !== this.buildToken) {
      for (const r of results) if (r.status === 'fulfilled') r.value.geometry.dispose();
      return;
    }

    let over = 0;
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i];
      const res = results[i];
      if (res.status !== 'fulfilled') {
        console.error(`[LabMode] ${SPECIES[i].id} failed`, res.reason);
        slot.card = { ...slot.card, triangles: 0, voxels: 0, ms: 0, over: true };
        continue;
      }
      const built = res.value;
      if (slot.mesh) {
        this.group.remove(slot.mesh);
        slot.pivot.remove(slot.mesh);
      }
      slot.built?.geometry.dispose();
      slot.built = built;

      const mesh = new THREE.Mesh(built.geometry, slot.material);
      mesh.frustumCulled = true;
      slot.pivot.add(mesh);
      slot.mesh = mesh;

      if (built.triangleCount > PERF.maxTriangleBudget) over++;
      slot.card = {
        id: SPECIES[i].id,
        name: SPECIES[i].name,
        triangles: built.triangleCount,
        voxels: built.solidVoxels,
        ms: built.buildMs,
        over: built.triangleCount > PERF.maxTriangleBudget,
      };
    }

    this.layout();
    this.panel?.setCards(this.slots.map((s) => s.card));
    const wall = performance.now() - t0;
    this.panel?.setStatus(
      over === 0
        ? `${SPECIES.length} SPECIES · ALL UNDER ${PERF.maxTriangleBudget} TRI · ${wall.toFixed(0)} MS`
        : `${over} SPECIES OVER THE ${PERF.maxTriangleBudget} TRI BUDGET`,
    );
  }

  /** grid the gallery from the real mesh extents so nothing overlaps as it turntables */
  private layout(): void {
    let extent = 1;
    for (const s of this.slots) {
      if (!s.built) continue;
      extent = Math.max(extent, s.built.size.x, s.built.size.z);
    }
    const pitch = extent + LAB.spacing;
    const cols = LAB.perRow;
    const rows = Math.ceil(this.slots.length / cols);

    for (let i = 0; i < this.slots.length; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      this.slots[i].pivot.position.set(
        (col - (cols - 1) / 2) * pitch,
        0,
        (row - (rows - 1) / 2) * pitch,
      );
    }

    this.ctx.camera.frameGallery(0, 0, cols * pitch + LAB.spacing);
    this.laidOut = true;
  }

  fixedUpdate(_dt: number): void {
    /* the gallery is presentation-only; nothing is simulated at a fixed rate here */
  }

  render(_alpha: number, dt: number, time: number): void {
    this.seafloor.update(time, 0);
    const swim = swimParamsForSpeed(MOVEMENT.cruiseSpeed * GALLERY_SPEED_FRACTION, MOVEMENT.cruiseSpeed);
    for (const slot of this.slots) {
      if (this.spin) slot.pivot.rotation.y += LAB.turntableSpeed * dt;
      slot.material.setTime(time);
      slot.material.setSwim(swim.amp, swim.speed);
    }
    if (!this.laidOut) this.ctx.camera.frameGallery(0, 0, LAB.perRow * LAB.spacing * 2);
  }

  resize(_width: number, _height: number): void {
    /* frameGallery is recomputed on layout; aspect changes are handled by the camera */
  }

  debugRows(): Array<[string, string]> {
    const tris = this.slots.reduce((a, s) => a + s.card.triangles, 0);
    const worst = this.slots.reduce((a, s) => Math.max(a, s.card.triangles), 0);
    return [
      ['species', String(this.slots.length)],
      ['tris total', String(tris)],
      ['tris worst', `${worst} / ${PERF.maxTriangleBudget}`],
      ['spin', this.spin ? 'ON' : 'OFF'],
      ['pending', String(this.ctx.mesh.pending)],
    ];
  }

  dispose(): void {
    this.exit();
    this.seafloor.dispose();
    for (const s of this.slots) {
      s.built?.geometry.dispose();
      s.material.dispose();
    }
  }
}
